// Motor Health Monitoring Engine
// EWMA + Statistical Limits + Slope + Cycle Time + Time-to-Failure + Multi-Sensor Fusion

import { detectPatterns, FailurePattern } from './patternEngine';

const ALPHA            = 0.3;  // more responsive to changes
const BASELINE_CYCLES  = 20;
const SLOPE_WINDOW     = 10;
const I_THRESHOLD_MULT = 2.0;
const CT_SENSITIVITY   = 0.2;
const ADAPT_WINDOW     = 50;
const ADAPT_SHIFT_THRESH = 1.5;
const MIN_SIGMA        = 0.5;  // floor sigma so UCL lines are always meaningful

export type AlertStage = 'CALIBRATING' | 'NORMAL' | 'EARLY' | 'MID' | 'LATE';

export interface MotorHealthResult {
  stage:         AlertStage;
  ewma:          number;
  slope:         number;
  cycletime:     number;
  ctBaseline:    number;
  mu:            number;
  sigma:         number;
  ucl2:          number;
  ucl3:          number;
  ttf:           number;
  dataPoints:    number;
  calibrating:   boolean;
  baselineShift: boolean;   // true when operating point has shifted significantly
  shiftDelta:    number;    // how many sigma the new mu differs from old mu
}

// ── Per-machine fused result ───────────────────────────────────
export interface ConfidenceBreakdown {
  overall:       number;  // 0–99 final score
  dataMaturity:  number;  // how many cycles collected vs ideal
  slopeConsistency: number; // how steady the trend is across sensors
  sensorAgreement:  number; // how many sensors agree on the stage
  signalStrength:   number; // how far EWMA is above UCL (clear signal)
  stageStability:   number; // how long the current stage has been held
}

export interface FusedMachineHealth {
  machineId:           string;
  healthScore:         number;
  stage:               AlertStage;
  dominantSensor:      string;
  sensorResults:       Record<string, MotorHealthResult>;
  calibrating:         boolean;
  ttf:                 number;
  avgSlope:            number;
  confidence:          number;
  confidenceBreakdown: ConfidenceBreakdown;
  detectedPatterns:    FailurePattern[];
  baselineShiftSensors: { name: string; delta: number; prevMu: number; newMu: number }[];
}

const CHART_WINDOW = 30; // keep last 30 points for the live chart

interface SensorState {
  ewmaHistory:  number[];
  rawHistory:   number[];
  chartHistory: number[];
  baseline:     number[];
  adaptWindow:  number[];
  mu:           number;
  sigma:        number;
  prevMu:       number;
  ewma:         number;
  dataPoints:   number;
  ctBaseline:   number;
  calibrated:   boolean;
  baselineShift: boolean;
  shiftDelta:    number;
  // hold counters — stage must be true for N cycles before firing
  earlyHold:    number;
  midHold:      number;
  lateHold:     number;
  activeStage:  AlertStage;
}

const EARLY_HOLD = 3;  // cycles EARLY must persist before alerting
const MID_HOLD   = 3;
const LATE_HOLD  = 2;

const STAGE_SCORE: Record<AlertStage, number> = {
  CALIBRATING: 100, NORMAL: 100, EARLY: 70, MID: 40, LATE: 10,
};

// tracks how many consecutive cycles each machine has held its current stage
const stageHoldCycles: Map<string, { stage: AlertStage; cycles: number }> = new Map();

class MotorHealthEngine {
  private states: Map<string, SensorState> = new Map();

  // ── Single sensor update ───────────────────────────────────
  update(key: string, value: number): MotorHealthResult {
    let s = this.states.get(key);
    if (!s) {
      s = { ewmaHistory: [], rawHistory: [], chartHistory: [], baseline: [],
            adaptWindow: [], mu: 0, sigma: 0, prevMu: 0,
            ewma: value, dataPoints: 0, ctBaseline: 2.0,
            calibrated: false, baselineShift: false, shiftDelta: 0,
            earlyHold: 0, midHold: 0, lateHold: 0, activeStage: 'NORMAL' };
      this.states.set(key, s);
    }

    s.dataPoints += 1;

    if (!s.calibrated) {
      s.baseline.push(value);
      s.ewma = ALPHA * value + (1 - ALPHA) * s.ewma;
      s.ewmaHistory.push(s.ewma);
      if (s.ewmaHistory.length > SLOPE_WINDOW + 1) s.ewmaHistory.shift();
      s.rawHistory.push(value);
      s.chartHistory.push(s.ewma);
      if (s.chartHistory.length > CHART_WINDOW) s.chartHistory.shift();
      if (s.rawHistory.length > CHART_WINDOW) s.rawHistory.shift();
      if (s.baseline.length >= BASELINE_CYCLES) {
        const n = s.baseline.length;
        s.mu    = s.baseline.reduce((a, b) => a + b, 0) / n;
        const rawSigma = Math.sqrt(s.baseline.reduce((a, b) => a + (b - s.mu) ** 2, 0) / n);
        s.sigma = Math.max(rawSigma, MIN_SIGMA);
        s.calibrated = true;
      }
      return {
        stage: 'CALIBRATING', ewma: s.ewma, slope: 0,
        cycletime: s.ctBaseline, ctBaseline: s.ctBaseline,
        mu: s.mu, sigma: s.sigma,
        ucl2: s.mu + 2 * s.sigma, ucl3: s.mu + 3 * s.sigma,
        ttf: -1, dataPoints: s.dataPoints, calibrating: true,
        baselineShift: false, shiftDelta: 0,
      };
    }

    s.ewma = ALPHA * value + (1 - ALPHA) * s.ewma;
    s.ewmaHistory.push(s.ewma);
    if (s.ewmaHistory.length > SLOPE_WINDOW + 1) s.ewmaHistory.shift();
    s.rawHistory.push(value);
    s.chartHistory.push(s.ewma);
    if (s.chartHistory.length > CHART_WINDOW) s.chartHistory.shift();
    if (s.rawHistory.length > CHART_WINDOW) s.rawHistory.shift();

    // ── Adaptive baseline re-calibration ─────────────────────────────
    s.adaptWindow.push(value);
    if (s.adaptWindow.length > ADAPT_WINDOW) s.adaptWindow.shift();

    s.baselineShift = false;
    s.shiftDelta    = 0;

    if (s.adaptWindow.length === ADAPT_WINDOW && s.dataPoints % ADAPT_WINDOW === 0) {
      const n      = s.adaptWindow.length;
      const newMu  = s.adaptWindow.reduce((a, b) => a + b, 0) / n;
      const newSig = Math.sqrt(s.adaptWindow.reduce((a, b) => a + (b - newMu) ** 2, 0) / n);
      const delta  = Math.abs(newMu - s.mu) / (s.sigma || 1);

      if (delta > ADAPT_SHIFT_THRESH) {
        // Operating point has shifted — flag it, then re-learn
        s.baselineShift = true;
        s.shiftDelta    = +delta.toFixed(2);
        s.prevMu        = s.mu;
        s.mu            = newMu;
        s.sigma         = Math.max(newSig > 0 ? newSig : s.sigma, MIN_SIGMA);
      }
    }

    const ucl2  = s.mu + 2 * s.sigma;
    const ucl3  = s.mu + 3 * s.sigma;
    const slope = s.ewmaHistory.length >= SLOPE_WINDOW
      ? (s.ewma - s.ewmaHistory[s.ewmaHistory.length - SLOPE_WINDOW]) / SLOPE_WINDOW
      : 0;
    const cycletime  = s.ctBaseline + CT_SENSITIVITY * Math.max(0, s.ewma - s.mu);
    const iThreshold = s.mu + I_THRESHOLD_MULT * (ucl3 - s.mu);
    const ttf        = slope > 0.001 ? Math.max(0, Math.round((iThreshold - s.ewma) / slope)) : -1;

    // ── Stage detection with hold requirement ─────────────────
    const rawStage: AlertStage =
      (s.ewma > ucl3 && slope > 0.003) ? 'LATE'  :
      (s.ewma > ucl2 && slope > 0.001) ? 'MID'   :
      (slope > 0.0005 && s.ewma > s.mu + s.sigma * 0.5) ? 'EARLY' : 'NORMAL';

    // Increment hold counter only if raw stage matches, else reset
    s.lateHold  = rawStage === 'LATE'  ? s.lateHold  + 1 : 0;
    s.midHold   = rawStage === 'MID'   ? s.midHold   + 1 : 0;
    s.earlyHold = rawStage === 'EARLY' ? s.earlyHold + 1 : 0;

    // Only promote to a stage once hold threshold is met
    let stage: AlertStage = 'NORMAL';
    if      (s.lateHold  >= LATE_HOLD)  stage = 'LATE';
    else if (s.midHold   >= MID_HOLD)   stage = 'MID';
    else if (s.earlyHold >= EARLY_HOLD) stage = 'EARLY';
    s.activeStage = stage;

    return {
      stage, ewma: s.ewma, slope, cycletime,
      ctBaseline: s.ctBaseline, mu: s.mu, sigma: s.sigma,
      ucl2, ucl3, ttf, dataPoints: s.dataPoints, calibrating: false,
      baselineShift: s.baselineShift, shiftDelta: s.shiftDelta,
    };
  }

  // ── Multi-sensor fusion for one machine ───────────────────
  fuse(machineId: string, sensors: { sensorId: string; name: string; value: number }[]): FusedMachineHealth {
    const STAGE_ORDER: AlertStage[] = ['LATE', 'MID', 'EARLY', 'NORMAL', 'CALIBRATING'];
    const sensorResults: Record<string, MotorHealthResult> = {};

    let worstStage: AlertStage    = 'NORMAL';
    let dominantSensor            = '';
    let shortestTTF               = -1;
    let totalSlope                = 0;
    let calibratingCount          = 0;
    let totalDataPoints           = 0;
    let slopeVariances: number[]  = [];

    sensors.forEach(({ sensorId, name, value }) => {
      const key    = `${machineId}::${sensorId}`;
      const result = this.update(key, value);
      sensorResults[name] = result;

      if (result.calibrating) { calibratingCount++; return; }

      totalSlope      += result.slope;
      totalDataPoints += result.dataPoints;
      slopeVariances.push(result.slope);

      // Track worst stage
      if (STAGE_ORDER.indexOf(result.stage) < STAGE_ORDER.indexOf(worstStage)) {
        worstStage     = result.stage;
        dominantSensor = name;
      }

      // Track shortest TTF
      if (result.ttf > 0 && (shortestTTF === -1 || result.ttf < shortestTTF)) {
        shortestTTF = result.ttf;
      }
    });

    const activeSensors = sensors.length - calibratingCount;
    const avgSlope      = activeSensors > 0 ? totalSlope / activeSensors : 0;

    // Health score
    const healthScore = activeSensors > 0
      ? Math.round(
          Object.values(sensorResults)
            .filter(r => !r.calibrating)
            .reduce((sum, r) => sum + STAGE_SCORE[r.stage], 0) / activeSensors
        )
      : 100;

    // ── 5-Factor Confidence Model ──────────────────────────────────
    const avgDataPoints = totalDataPoints / Math.max(activeSensors, 1);

    // 1. Data maturity: 0→100 as data points grow from 20 to 200
    const dataMaturity = Math.round(Math.min((avgDataPoints - BASELINE_CYCLES) / (200 - BASELINE_CYCLES), 1) * 100);

    // 2. Slope consistency: low std-dev across sensors = consistent trend
    const slopeStdDev = slopeVariances.length > 1
      ? Math.sqrt(slopeVariances.reduce((s, v) => s + (v - avgSlope) ** 2, 0) / slopeVariances.length)
      : 0;
    const slopeConsistency = Math.round(Math.max(0, 1 - slopeStdDev / (Math.abs(avgSlope) + 0.001)) * 100);

    // 3. Sensor agreement: % of active sensors that agree on the worst stage
    const activeResults = Object.values(sensorResults).filter(r => !r.calibrating);
    const agreeCount    = activeResults.filter(r => r.stage === worstStage).length;
    const sensorAgreement = activeSensors > 0 ? Math.round((agreeCount / activeSensors) * 100) : 0;

    // 4. Signal strength: how far the dominant sensor's EWMA is above UCL2 (0→100)
    const domResult = sensorResults[dominantSensor];
    const signalStrength = domResult && domResult.ucl2 > 0
      ? Math.round(Math.min(Math.max((domResult.ewma - domResult.ucl2) / (domResult.ucl3 - domResult.ucl2 + 0.001), 0), 1) * 100)
      : 0;

    // 5. Stage stability: how many consecutive cycles the stage has been held (capped at 20)
    const holdKey  = machineId;
    const holdData = stageHoldCycles.get(holdKey);
    let holdCycles = 1;
    if (holdData && holdData.stage === worstStage) {
      holdCycles = holdData.cycles + 1;
    }
    stageHoldCycles.set(holdKey, { stage: worstStage, cycles: holdCycles });
    const stageStability = Math.round(Math.min(holdCycles / 20, 1) * 100);

    // Weighted final score
    const confidence = Math.min(
      Math.round(
        dataMaturity      * 0.25 +
        slopeConsistency  * 0.25 +
        sensorAgreement   * 0.20 +
        signalStrength    * 0.15 +
        stageStability    * 0.15
      ),
      99
    );

    const confidenceBreakdown: ConfidenceBreakdown = {
      overall: confidence,
      dataMaturity,
      slopeConsistency,
      sensorAgreement,
      signalStrength,
      stageStability,
    };

    // ── Pattern recognition ────────────────────────────────────
    const detectedPatterns = detectPatterns(sensorResults);

    // ── Baseline shift sensors ────────────────────────────────
    const baselineShiftSensors = sensors
      .map(({ name, sensorId }) => {
        const key = `${machineId}::${sensorId}`;
        const s   = (this as any).states.get(key);
        if (!s || !s.baselineShift) return null;
        return { name, delta: s.shiftDelta, prevMu: +s.prevMu.toFixed(3), newMu: +s.mu.toFixed(3) };
      })
      .filter(Boolean) as { name: string; delta: number; prevMu: number; newMu: number }[];

    return {
      machineId,
      healthScore,
      stage:                calibratingCount === sensors.length ? 'CALIBRATING' : worstStage,
      dominantSensor,
      sensorResults,
      calibrating:          calibratingCount > 0,
      ttf:                  shortestTTF,
      avgSlope:             +avgSlope.toFixed(4),
      confidence,
      confidenceBreakdown,
      detectedPatterns,
      baselineShiftSensors,
    };
  }

  // Returns chart-ready data for a sensor key
  getChartData(machineId: string, sensorId: string): { t: number; raw: number; ewma: number; ucl2: number; ucl3: number }[] {
    const key = `${machineId}::${sensorId}`;
    const s   = this.states.get(key);
    if (!s || !s.calibrated) return [];
    const ucl2 = s.mu + 2 * s.sigma;
    const ucl3 = s.mu + 3 * s.sigma;
    return s.chartHistory.map((ewma, i) => ({
      t:    i + 1,
      raw:  +( s.rawHistory[i] ?? ewma).toFixed(3),
      ewma: +ewma.toFixed(3),
      ucl2: +ucl2.toFixed(3),
      ucl3: +ucl3.toFixed(3),
    }));
  }

  resetKey(key: string) { this.states.delete(key); }

  reset() { this.states.clear(); }
}

export const motorHealthEngine = new MotorHealthEngine();
